import { useState, useEffect } from "react";
import { call as rpc } from "../../../common/jsonRpcRoutes";
import { loadAuthComponentsScript, isScriptLoaded } from "../../../common/scriptSecurity/secureScriptLoader";

interface AuthComponentsConfig {
  version: string;
  authApiUrl: string;
  clientId: string;
  clientSecret: string;
  releaseStage: string;
  scriptUrl?: string;
}

interface UseAuthComponentsResult {
  /** Whether the auth-components script is loaded and ready to use */
  ready: boolean;
  /** Configuration to pass as attributes to <dmn-sign-in> */
  config: AuthComponentsConfig | null;
  /** Whether auth-components is available (configured on server) */
  available: boolean;
  /** Any error that occurred during loading */
  error: string | null;
}

/**
 * Loads a script by URL directly, bypassing the authorised-scripts registry.
 * Used only for local development when AUTH_COMPONENTS_SCRIPT_URL is configured.
 */
function loadLocalScript(url: string): Promise<HTMLScriptElement> {
  if (isScriptLoaded(url)) {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${url}"]`);
    if (existing) return Promise.resolve(existing);
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.type = "module";
    script.crossOrigin = "anonymous";
    script.src = url;
    script.onload = () => resolve(script);
    script.onerror = () => reject(new Error(`Failed to load local auth-components script from ${url}`));
    document.body.appendChild(script);
  });
}

/**
 * Fetches auth-components configuration from the server and loads the
 * external dmn-auth-components.js script. Returns readiness state and
 * configuration for rendering the <dmn-sign-in> web component.
 *
 * When the server returns 404 (auth-components not configured), the hook
 * returns `{ available: false }` without treating it as a fatal error.
 */
export function useAuthComponents(): UseAuthComponentsResult {
  const [config, setConfig] = useState<AuthComponentsConfig | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        const result = await rpc("/auth-components/config", {});
        if (cancelled) return;
        setConfig(result);

        if (result.releaseStage !== "production" && result.scriptUrl) {
          await loadLocalScript(result.scriptUrl);
        } else {
          await loadAuthComponentsScript(result.version);
        }
        if (cancelled) return;
        setReady(true);
      } catch (err: unknown) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load auth components");
      }
    }

    init();
    return () => {
      cancelled = true;
    };
  }, []);

  return {
    ready,
    config,
    available: config !== null,
    error,
  };
}
